import { useState } from 'react';
import { Link, Navigate } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { AdminChurch } from '../../shared/api/types';
import { EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { STATUS_LABELS, isLapsed } from '../billing/format';
import { useMe } from '../session/SessionProvider';
import { DENOMINATION_STATUS, type ChurchDenomination } from '../denomination/denomination';

export const formatDay = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

/** Panel de la administración de KEORA: todas las Iglesias registradas (solo Superadmin). */
export function AdminChurchesPage() {
  const me = useMe();
  const [query, setQuery] = useState('');
  const churches = useAsync((signal) => api.get<{ items: AdminChurch[] }>('/multichurch/admin/churches', signal).then((r) => r.items), []);
  const denominations = useAsync((signal) => api.get<{ items: Record<string, ChurchDenomination> }>('/multichurch/admin/denominations', signal).then((r) => r.items).catch(() => ({}) as Record<string, ChurchDenomination>), []);
  const dens = denominations.status === 'ready' ? denominations.data : {};
  const denPending = Object.values(dens).filter((d) => d.status === 'pending').length;
  if (!me.isPlatformAdmin) return <Navigate to="/app" replace />;

  const q = query.trim().toLowerCase();
  const items =
    churches.status === 'ready'
      ? churches.data.filter((c) => !q || [c.name, c.slug, c.founder?.name, c.founder?.email].some((v) => v?.toLowerCase().includes(q)))
      : [];
  const totals =
    churches.status === 'ready'
      ? {
          churches: churches.data.length,
          members: churches.data.reduce((n, c) => n + c.members.active, 0),
          paying: churches.data.filter((c) => c.subscription?.status === 'active').length,
          trial: churches.data.filter((c) => c.subscription?.status === 'trialing').length,
          pending: churches.data.filter((c) => c.subscription?.status === 'pending').length,
        }
      : null;

  return (
    <div className="page">
      <header className="page__header">
        <h1>Iglesias</h1>
        <p className="muted">Todas las iglesias registradas en KEORA. Solo tú, como administración de KEORA, ves esta página; cada consulta queda en la auditoría.</p>
      </header>

      {totals ? (
        <ul className="admin-stats" aria-label="Resumen">
          <li>
            <strong>{totals.churches}</strong> iglesias
          </li>
          <li>
            <strong>{totals.members}</strong> miembros
          </li>
          <li>
            <strong>{totals.paying}</strong> pagando
          </li>
          <li>
            <strong>{totals.trial}</strong> en prueba
          </li>
          {totals.pending ? (
            <li>
              <strong>{totals.pending}</strong> por activar
            </li>
          ) : null}
          {denPending ? (
            <li>
              <strong>{denPending}</strong> {denPending === 1 ? 'denominación por verificar' : 'denominaciones por verificar'}
            </li>
          ) : null}
        </ul>
      ) : null}

      <NetworksAdmin />

      <div className="field">
        <label htmlFor="church-search">Buscar</label>
        <input id="church-search" type="search" placeholder="Nombre, dirección, fundador o correo" value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>

      {churches.status === 'loading' ? <Loading /> : null}
      {churches.status === 'error' ? <ErrorState error={churches.error} onRetry={churches.reload} /> : null}
      {churches.status === 'ready' && items.length === 0 ? <EmptyState title={q ? 'Ninguna iglesia coincide.' : 'Todavía no hay iglesias registradas.'} /> : null}

      <ul className="admin-list">
        {items.map((c) => {
          const sub = c.subscription;
          return (
            <li key={c.id}>
              <Link to={`/app/admin/iglesias/${c.id}`} className="admin-row">
                <span className="admin-row__main">
                  <strong>{c.name}</strong>
                  <span className="small muted">
                    /{c.slug} · desde {formatDay(c.createdAt)}
                    {c.founder ? ` · ${c.founder.name}${c.founder.email ? ` (${c.founder.email})` : ''}` : ''}
                  </span>
                </span>
                <span className="admin-row__meta">
                  <span className="small">
                    {c.members.active} {c.members.active === 1 ? 'miembro' : 'miembros'}
                    {c.members.pending > 0 ? ` · ${c.members.pending} por aprobar` : ''}
                  </span>
                  {c.status === 'suspended' ? <span className="badge badge--warn">Suspendida</span> : null}
                  {dens[c.id] ? (
                    <span className={`badge ${dens[c.id]!.status === 'verified' ? 'badge--active' : dens[c.id]!.status === 'pending' ? 'badge--pending' : 'badge--warn'}`}>
                      {dens[c.id]!.name} · {DENOMINATION_STATUS[dens[c.id]!.status]}
                    </span>
                  ) : null}
                  {sub ? (
                    <span className={`badge ${isLapsed(sub.status) ? 'badge--warn' : 'badge--active'}`}>
                      {sub.planName} · {STATUS_LABELS[sub.status]}
                    </span>
                  ) : (
                    <span className="badge">Sin plan</span>
                  )}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>

      <section className="admin-settings" aria-labelledby="keora-settings">
        <h2 id="keora-settings">Ajustes de KEORA</h2>
        <p className="small muted">Aplican a toda la plataforma, no a una sola iglesia.</p>
        <ActivationSetting />
        <ModulesAdmin />
      </section>
    </div>
  );
}

/** ¿Las iglesias nuevas empiezan su prueba solas o esperan el primer pago / activación manual? */
function ActivationSetting() {
  const setting = useAsync((signal) => api.get<{ newChurchActivation: 'auto' | 'manual' }>('/saas/admin/settings', signal), []);
  const [error, setError] = useState<string | null>(null);
  if (setting.status !== 'ready') return null;
  async function change(value: 'auto' | 'manual') {
    if (!window.confirm('Esto aplica a todas las iglesias nuevas. ¿Cambiar?')) return;
    setError(null);
    try {
      await api.put('/saas/admin/settings', { newChurchActivation: value });
      setting.reload();
    } catch (e) {
      setError(errorMessage(e));
    }
  }
  return (
    <div className="field">
      <label htmlFor="new-church-activation">Iglesias nuevas</label>
      <select id="new-church-activation" value={setting.data.newChurchActivation} onChange={(e) => void change(e.target.value as 'auto' | 'manual')}>
        <option value="manual">Esperan su pago o que yo las active</option>
        <option value="auto">Empiezan su prueba solas</option>
      </select>
      {error ? <p className="field__hint" role="alert">{error}</p> : null}
    </div>
  );
}

type AdminModule = { code: string; name: string; unit: string; monthlyPriceCents: number | null; monthlyLimit: number; churches: number; ready: boolean };

/** Módulos extra con costo: precio mensual y tope de uso que pagan las iglesias que los contratan. */
function ModulesAdmin() {
  const list = useAsync((signal) => api.get<{ items: AdminModule[] }>('/saas/admin/modules', signal).then((r) => r.items), []);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  if (list.status !== 'ready') return null;
  return (
    <details className="card">
      <summary>Módulos extra ({list.data.length})</summary>
      {list.data.map((m) => (
        <form
          key={m.code}
          className="family-add"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            const price = String(f.get('price') ?? '').trim();
            setError(null);
            setSaved(null);
            api
              .put(`/saas/admin/modules/${m.code}`, { monthlyPriceCents: price === '' ? null : Math.round(Number(price) * 100), monthlyLimit: Number(f.get('limit')) })
              .then(() => {
                setSaved(m.code);
                list.reload();
              })
              .catch((err) => setError(errorMessage(err)));
          }}
        >
          <p>
            <strong>{m.name}</strong> <span className="small muted">· {m.churches} {m.churches === 1 ? 'iglesia lo tiene' : 'iglesias lo tienen'}</span>
          </p>
          {!m.ready ? <p className="small" role="alert">Falta configurar la IA en el servidor (setup/configurar-ia.sh). Mientras tanto ninguna iglesia lo puede contratar.</p> : null}
          <div className="field">
            <label htmlFor={`mod-price-${m.code}`}>Precio mensual (en la moneda de cada plan)</label>
            <input id={`mod-price-${m.code}`} name="price" type="number" min={0} step="0.01" defaultValue={m.monthlyPriceCents === null ? '' : m.monthlyPriceCents / 100} placeholder="Vacío = aún no se ofrece" />
          </div>
          <div className="field">
            <label htmlFor={`mod-limit-${m.code}`}>Tope de {m.unit} al mes por iglesia</label>
            <input id={`mod-limit-${m.code}`} name="limit" type="number" min={1} max={10000} required defaultValue={m.monthlyLimit} />
          </div>
          {error ? <p className="field__hint" role="alert">{error}</p> : null}
          {saved === m.code ? <p className="field__hint">Guardado. Un precio nuevo aplica desde el siguiente pago de cada iglesia.</p> : null}
          <button type="submit" className="btn btn--primary">Guardar</button>
        </form>
      ))}
    </details>
  );
}

/** Asociaciones, concilios, distritos y redes (las crea la administración de KEORA). */
function NetworksAdmin() {
  const list = useAsync((signal) => api.get<{ items: { id: string; name: string; kind: string; churches: number; parent_id: string | null }[] }>('/multichurch/networks', signal).then((r) => r.items), []);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [overseersOf, setOverseersOf] = useState<string | null>(null);
  if (list.status !== 'ready') return null;
  const parentName = (id: string | null) => list.data.find((n) => n.id === id)?.name;
  return (
    <details className="card">
      <summary>Asociaciones y redes ({list.data.length})</summary>
      <ul className="list">
        {list.data.map((n) => (
          <li key={n.id} className="list__item list__item--actions">
            <span>
              {n.name}{' '}
              <span className="small muted">
                {n.kind}
                {n.parent_id ? ` de ${parentName(n.parent_id) ?? '…'}` : ''} · {n.churches} iglesias
              </span>
            </span>
            <button type="button" className="btn btn--ghost" onClick={() => setOverseersOf(overseersOf === n.id ? null : n.id)}>
              Presbíteros
            </button>
            {overseersOf === n.id ? <Overseers associationId={n.id} /> : null}
          </li>
        ))}
      </ul>
      {open ? (
        <form className="family-add" onSubmit={(e) => {
          e.preventDefault();
          const f = new FormData(e.currentTarget);
          setError(null);
          api.post('/multichurch/networks', { name: String(f.get('name') ?? '').trim(), kind: String(f.get('kind')), description: String(f.get('description') ?? '').trim() || null, parentId: String(f.get('parentId') ?? '') || null })
            .then(() => { setOpen(false); list.reload(); })
            .catch((err) => setError(errorMessage(err)));
        }}>
          {error ? <p className="field__hint" role="alert">{error}</p> : null}
          <div className="field"><label htmlFor="net-name">Nombre</label><input id="net-name" name="name" required minLength={2} maxLength={150} /></div>
          <div className="field"><label htmlFor="net-kind">Tipo</label><select id="net-kind" name="kind"><option value="asociacion">Asociación</option><option value="concilio">Concilio</option><option value="distrito">Distrito</option><option value="seccion">Sección</option><option value="fraternidad">Fraternidad</option><option value="red">Red</option></select></div>
          <div className="field"><label htmlFor="net-parent">Pertenece a</label><select id="net-parent" name="parentId" defaultValue=""><option value="">—</option>{list.data.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}</select></div>
          <div className="field"><label htmlFor="net-desc">Descripción</label><input id="net-desc" name="description" maxLength={1000} /></div>
          <button type="submit" className="btn btn--primary">Crear</button>
        </form>
      ) : <button type="button" className="btn btn--secondary" onClick={() => setOpen(true)}>Nueva asociación o red</button>}
    </details>
  );
}

/** Presbíteros, superintendentes y secretarios de una sección o distrito (por su correo de KEORA). */
function Overseers({ associationId }: { associationId: string }) {
  const base = `/multichurch/admin/associations/${associationId}/overseers`;
  const list = useAsync((signal) => api.get<{ items: { personId: string; name: string; title: string }[] }>(base, signal).then((r) => r.items), [base]);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="overseers">
      {list.status === 'ready' && list.data.length === 0 ? <p className="small muted">Sin presbítero asignado.</p> : null}
      {list.status === 'ready' ? (
        <ul className="list">
          {list.data.map((o) => (
            <li key={o.personId} className="list__item list__item--actions">
              <span>{o.name} <span className="small muted">· {o.title}</span></span>
              <button type="button" className="btn btn--ghost" onClick={() => api.delete(`${base}/${o.personId}`).then(list.reload).catch((err) => setError(errorMessage(err)))}>Quitar</button>
            </li>
          ))}
        </ul>
      ) : null}
      <form className="form form--inline" onSubmit={(e) => {
        e.preventDefault();
        const f = new FormData(e.currentTarget);
        setError(null);
        const form = e.currentTarget;
        api.post(base, { email: String(f.get('email') ?? '').trim(), title: String(f.get('title')) })
          .then(() => { form.reset(); list.reload(); })
          .catch((err) => setError(errorMessage(err)));
      }}>
        {error ? <p className="field__hint" role="alert">{error}</p> : null}
        <div className="field"><label htmlFor={`ov-email-${associationId}`}>Correo de su cuenta de KEORA</label><input id={`ov-email-${associationId}`} name="email" type="email" required /></div>
        <div className="field"><label htmlFor={`ov-title-${associationId}`}>Cargo</label><select id={`ov-title-${associationId}`} name="title"><option value="presbitero">Presbítero</option><option value="superintendente">Superintendente</option><option value="secretario">Secretario</option></select></div>
        <button type="submit" className="btn btn--secondary">Asignar</button>
      </form>
    </div>
  );
}
